import useForkRef from '@mui/material/utils/useForkRef';
import _ from 'lodash';
import { useCallback, useEffect, useRef } from 'react';
import { get, useController, useFormContext } from 'react-hook-form';
import { useErrorMessage } from './use-error-message';

// Add string map to allow for values to be interpolated in error message

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useField({
  name,
  defaultValue,
  disableRefWarning,
  ...context
}) {
  const formMethods = useFormContext();
  if (!formMethods) {
    throw new Error('Could not get form context. Did you forget to use FormProvider?');
  }
  const controllerMethods = useController({
    name,
    defaultValue,
    control: formMethods.control
  });

  /**
   * Extract the final field name from a nested path.
   *
   * `foo.bar.baz` -> `baz`
   *
   * `foo` -> `foo`
   */
  const errorLabel = name.replace(/.*\./u, '');
  /**
   * Errors do not seem stable in react-hook-form.
   * This may need changed to something more robust.
   */
  const errorMessage = useErrorMessage(get(formMethods.formState.errors, name), {
    label: errorLabel === 'avbId' ? 'AVB ID' : _.startCase(errorLabel),
    ...context
  });
  const hookRef = useRef(null);
  const ref = useForkRef(controllerMethods.field.ref, hookRef);
  useErrorFocus(errorMessage, hookRef);
  return {
    ...controllerMethods,
    field: {
      ...controllerMethods.field,
      ref: useUnplacedRefWarning(ref, name, disableRefWarning)
    },
    errorMessage,
    formMethods
  };
}

/**
 * Log a warning if the field's ref is not placed.
 *
 * Field refs should (almost) always be placed so that react-hook-form
 * can scroll the field in to view when it has an error.
 */
// TODO Should this be disabled/noop in production?
function useUnplacedRefWarning(originalFieldRef, name, disableRefWarning) {
  const fieldRefPlaced = useRef(false);
  const fieldRef = useCallback(instance => {
    originalFieldRef?.(instance);
    fieldRefPlaced.current = !!instance;
  }, [originalFieldRef]);
  useEffect(() => {
    if (!fieldRefPlaced.current && !disableRefWarning) {
      console.warn('Ref was not placed for field %o.', name);
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [fieldRefPlaced.current]);
  return fieldRef;
}
function useErrorFocus(error, ref) {
  useEffect(() => {
    /**
     * Focus the field if it has an error. Normally, this
     * focus should be set automically by
     * `formMethods.setError(field, err, { shouldFocus: true })`
     * but it doesn't work for some reason (even when using
     * a registered input as included in the docs).
     */
    if (error) {
      ref.current?.focus();
    }
  }, [error, ref]);
}